import { PackageScanModesFormController } from '../controller/PackageScanModesFormController';
import { BaseWebComponentElement } from '../../../../framework/BaseWebComponentElement';
import { DeepPartial } from 'typeorm';
import { PackageData } from '../../../types/package-data';
import { Observable } from 'rxjs';
import { ScanModes } from '../types/ScanModes';
import { Feature } from '@nirvana/shared/constants';
import { PackageActionContext } from '../../types/PackageActionContext';
import { MonthlyProductType } from '../../../../license/types/MonthlyProductType';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { ProtectedEntitiesOptions } from '../types/ProtectedEntitiesTypes';

export class PackageScanModesFormWebComponent extends BaseWebComponentElement {
  public controller!: PackageScanModesFormController;
  private features: Feature[];
  private contextIsFrat: boolean;
  private _useDefaultValues;

  constructor() {
    super();
    this.initialSetup({
      controllerConstructor: PackageScanModesFormController,
    });
  }

  public get value(): DeepPartial<PackageData> {
    if (this.controller.view.dom.$packageScanModesFormField.Input.getFormValue() === ScanModes.Automatic) {
      const data: DeepPartial<PackageData> = this.controller.view.getAutomaticFormValue();
      this.ignoreFallbackTrue(data);
      return data;
    }
    return this.controller.view.formGroup.value;
  }

  public get valid$(): Observable<boolean> {
    return this.controller.view.formGroup.valid$;
  }

  public setFeatures(availableFeatures: Feature[]): void {
    this.features = availableFeatures;
  }

  public getFeatures(): Feature[] {
    return this.features;
  }

  public setContextFrat(contextIsFrat: boolean): void {
    this.contextIsFrat = contextIsFrat;
  }

  public getContextFrat(): boolean {
    return this.contextIsFrat;
  }

  public set useDefaultValues(useDefaultValues: boolean) {
    this._useDefaultValues = useDefaultValues;
  }

  public get useDefaultValues(): boolean {
    return this._useDefaultValues;
  }

  public setFormFieldAutoForEdr(): void {
    this.controller.setFormFieldAutoForEdr();
  }

  public setScanModeAuto(availableFeatures: Feature[]): void {
    this.controller.setScanModeAuto(availableFeatures);
  }

  public setPackageData(packageData: PackageData, availableFeatures: Feature[]): void {
    this.controller.view.setPackageData(packageData, availableFeatures).then().catch(console.error);
  }

  public setCustomScanModeFields(availableFeatures: Feature[]): void {
    this.controller.setCustomScanModeFields(availableFeatures);
  }

  public get formContext(): PackageActionContext {
    const context = this.getAttribute('context');

    if (!PackageActionContext.isValid(context)) {
      throw new Error(`PackagePanel context not valid: ${context}`);
    }

    return context as PackageActionContext;
  }

  public set formContext(context: PackageActionContext) {
    this.setAttribute('context', context);
  }

  /**
   * It is used to match the template/request from old packages section
   *
   * @param {DeepPartial<PackageData>} data
   * @private
   */
  private ignoreFallbackTrue(data: DeepPartial<PackageData>): void {
    if (data.settings?.scanMode) {
      for (const key of Object.keys(data.settings?.scanMode)) {
        if (data.settings.scanMode[key].fallback === true) {
          delete data.settings.scanMode[key].fallback;
        }
      }
    }
  }

  public get hasUnsavedChanges(): boolean {
    return this.controller.view.formGroup.hasUnsavedChanges;
  }

  public toggleVisibilityBasedOnProductType(productType: MonthlyProductType): void {
    const isEdrSelected: boolean = productType === MonthlyProductType.BitdefenderEdr;

    toggleVisibility(this, !isEdrSelected);
    if (isEdrSelected) {
      this.controller.view.setFormFieldAutoForEdr();
    }
  }

  public hasCustomModeOptions(scanType: ScanModes): boolean {
    if (scanType === ScanModes.Custom) {
      const customModeOptions: ProtectedEntitiesOptions = this.controller.view.getCustomModeOptions();
      return this.hasCentralScan(customModeOptions);
    }

    return false;
  }

  public hasCentralScan(protectedEntitiesOptions: ProtectedEntitiesOptions): boolean {
    return this.controller.hasCentralScan(protectedEntitiesOptions);
  }
}
